import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import Btn from '../../main-app/components/buttons/Btn';

const Container = styled.div`
  margin-bottom: 8px;
  max-width: 500px;
`;

const Title = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #354354;
  margin-bottom: 5px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    width: 100%;
    align-items: stretch;
  }
`;

const Input = styled.input<{ hasError?: boolean; isSuccess?: boolean }>`
  flex: 1;
  min-height: 48px;
  padding: 0 16px;
  border: 1px solid ${({ hasError }) => (hasError ? '#F44336' : '#DFE3E8')};
  border-radius: 4px;
  font-size: 16px;
  color: #354354;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: ${({ hasError, isSuccess }) =>
      hasError ? '#F44336' : isSuccess ? '#4CAF50' : '#4A90E2'};
    background-color: #ffffff;
  }

  &::placeholder {
    color: #a0a8b2;
  }

  &:disabled {
    background-color: #f2f2f2;
    cursor: not-allowed;
  }
`;

const Message = styled.p<{ isError?: boolean }>`
  margin-top: 8px;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ isError }) => (isError ? '#f74656' : '#44c496')};
  background-color: ${({ isError }) => (isError ? '#fee7e9' : '#44c49619')};
  width: fit-content;
  font-weight: 600;
`;

type PromoInputProps = {
  title?: string;
  placeholder?: string;
  buttonText?: string;
  onApplyPromo: (code: string) => Promise<void>;
  onSuccess?: (discount: { type: 'percentage' | 'fixed'; value: number }) => void;
  disabled?: boolean;
  price?: number;
  newPrice?: number;
  promoValidationStatus?: number;
};

export const PromoInput: React.FC<PromoInputProps> = ({
  title = "J'ai un code promo",
  placeholder = 'Code promo ou code de parrainage',
  buttonText = 'Appliquer',
  onApplyPromo,
  onSuccess,
  disabled = false,
  price,
  newPrice,
  promoValidationStatus,
}) => {
  const [promoCode, setPromoCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const promoFromUrl = localStorage.getItem('stage_booking_promo') || '';

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value);
    setHasError(false);
    setIsSuccess(false);
  }, []);

  const dispatchPriceChange = (oldPrice: number, newPrice: number) => {
    const event = new CustomEvent('priceChange', {
      detail: { oldPrice, newPrice },
    });

    window.dispatchEvent(event);
  };

  const handleApplyPromo = async () => {
    const code = promoCode || promoFromUrl;
    if (!code.trim()) {
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    setIsSuccess(false);

    await onApplyPromo(code);
  };

  useEffect(() => {
    if (promoValidationStatus === 200) {
      setIsSuccess(true);
      setHasError(false);
      dispatchPriceChange(price, newPrice);
    } else if (promoValidationStatus) {
      setHasError(true);
      setIsSuccess(false);
    }
    setIsLoading(false);
  }, [promoValidationStatus]);

  useEffect(() => {
    if (promoFromUrl) {
      setPromoCode(promoFromUrl);
      handleApplyPromo();
      dispatchPriceChange(price, newPrice);
    }
  }, [promoFromUrl]);

  const renderMessage = () => {
    if (hasError) {
      return <Message isError>Code promo invalide</Message>;
    }

    if (isSuccess) {
      return (
        <Message>
          Le code promo a été appliqué. Le prix du stage est désormais de {newPrice}€ (prix non
          remisé : {price}€)
        </Message>
      );
    }

    return null;
  };

  const isButtonDisabled = disabled || isLoading || !promoCode.trim() || isSuccess;

  return (
    <>
      {!promoFromUrl && (
        <Container>
          <Title>{title}</Title>

          <InputContainer>
            <Input
              type="text"
              placeholder={placeholder}
              value={promoCode}
              onChange={handleInputChange}
              hasError={hasError}
              isSuccess={isSuccess}
              disabled={disabled || isLoading || isSuccess}
            />
            <Btn
              type="button"
              text={buttonText}
              onClick={handleApplyPromo}
              disabled={isButtonDisabled}
              isLoading={isLoading}
            />
          </InputContainer>
        </Container>
      )}
      {renderMessage()}
    </>
  );
};
